import classNames from 'clsx';
import React, { type ReactElement, memo, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { IS_BIZ_USER, IS_CORP_USER, IS_PUBLIC_ALBUM, IS_PUBLIC_FOLDER } from 'reactApp/appHelpers/configHelpers';
import { abRequiredSignUpWhenDownloadingTouchSelector } from 'reactApp/appHelpers/featuresHelpers';
import { accentSaveToCloudButton } from 'reactApp/appHelpers/featuresHelpers/features/accentSaveToCloudButton';
import { tooltipLimitPublic } from 'reactApp/appHelpers/featuresHelpers/features/publicDownloadLimits';
import { FeatureSelector } from 'reactApp/appHelpers/featuresHelpers/FeatureSelector';
import { getFacesListRequestState } from 'reactApp/modules/faces/faces.selectors';
import { getWeblinkFromPublicId } from 'reactApp/modules/file/utils';
import { getPublicLimits, isOwnPublic } from 'reactApp/modules/public/public.selectors';
import { RequiredSignUpTouchSource } from 'reactApp/modules/requiredAuthorization/constants';
import { requiredAuthorizationHelpers } from 'reactApp/modules/requiredAuthorization/helpers';
import { getCurrentRouteId, getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getCurrentItem } from 'reactApp/modules/storage/storage.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { type RootState, store as reduxStore } from 'reactApp/store';
import { type IActionItem, useItems } from 'reactApp/ui/Mobile/ActionsCarousel/useItems';
import { Placeholder } from 'reactApp/ui/Placeholder/Placeholder';
import { DownloadTooltip } from 'reactApp/ui/ReactViewer/ViewerHeader/DownloadTooltip';
import { DownloadLimitsPromo } from 'reactApp/ui/SharingTooltips/DownloadLimitsPromo';
import { sendDwh } from 'reactApp/utils/ga';

import styles from './ActionsCarousel.css';

const defaultActionsFilter = () => true;

export const ActionsCarousel = memo(
    ({
        disabled = false,
        noMargins = false,
        actionsFilter = defaultActionsFilter,
        supportThemedPage = false,
    }: {
        disabled?: boolean;
        noMargins?: boolean;
        actionsFilter?(item: IActionItem): boolean;
        supportThemedPage?: boolean;
    }): ReactElement => {
        const [showTip, setShowTip] = useState(false);
        const storage = useSelector(getCurrentStorage);
        const downloadBtnRef = useRef<HTMLDivElement>(null);
        const items = useItems({ storage, downloadBtnRef }).filter(actionsFilter);
        const { isLoading } = useSelector(getFacesListRequestState);
        const isRequiredSingUpTouchSource = useSelector((state: RootState) =>
            requiredAuthorizationHelpers.isRequiredSingUpTouchSource(state, RequiredSignUpTouchSource.PUBLIC_TOOLBAR)
        );
        const isViewerActive = useSelector(ViewerSelectors.isViewerActive);
        // tempexp_17847-start
        const isPaidUser = useSelector(UserSelectors.isPaidUser);
        const routeId = useSelector(getCurrentRouteId);
        const weblink = getWeblinkFromPublicId(routeId);
        const currentItem = useSelector((state: RootState) => getCurrentItem(state));
        const downloadLimits = useSelector((state: RootState) => getPublicLimits(state, weblink || ''));

        const showDownloadLimitsPromo = tooltipLimitPublic && !IS_BIZ_USER && !IS_CORP_USER && !isPaidUser && downloadLimits?.left === 0;
        const [showDownloadPromo, setShowDownloadPromo] = useState(false);
        const isPublicOwner = useSelector(isOwnPublic);
        // tempexp_17847-end

        const requiredSingUpTooltip = useMemo(() => {
            return <DownloadTooltip btnText="Скачать" downloadRef={downloadBtnRef.current} isWhite closeOnOutsideClick />;
        }, [downloadBtnRef.current]);

        useEffect(() => {
            if (downloadBtnRef?.current && isRequiredSingUpTouchSource && IS_PUBLIC_FOLDER && !isViewerActive) {
                setShowTip(true);
            }
            if (downloadBtnRef?.current && IS_PUBLIC_FOLDER && showDownloadLimitsPromo) {
                sendDwh({ eventCategory: 'download', action: 'no_download_show' });
            }
        }, [downloadBtnRef?.current, isRequiredSingUpTouchSource, isViewerActive, showDownloadLimitsPromo]);

        if (IS_PUBLIC_FOLDER && isLoading && !IS_PUBLIC_ALBUM) {
            return (
                <div className={classNames(styles.root, styles.root_loading)}>
                    <Placeholder />
                </div>
            );
        }

        const onCustomClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            sendDwh({ eventCategory: 'download', action: 'no_download_click' });
            setShowDownloadPromo(true);
        };

        const handleClickClose = () => {
            setShowDownloadPromo(false);
        };

        return (
            <div
                className={classNames({
                    [styles.root]: true,
                    [styles.root_disabled]: disabled,
                    [styles.themedPage]: supportThemedPage,
                    [styles.no_margins]: noMargins,
                })}
            >
                <div className={styles.wrapper}>
                    {items.map((item) => (
                        <div
                            ref={item.ref}
                            className={classNames({
                                [styles.item]: true,
                                [styles.item_active]: item.active,
                                [styles[`item_${item.id}`]]: Boolean(item.id),
                                [styles.item_disabled]: item.id === 'download' && IS_PUBLIC_FOLDER && showDownloadLimitsPromo,
                                [styles.item_clone_accent]:
                                    item.id === 'clone' && UserSelectors.isAnonymous(reduxStore.getState()) && accentSaveToCloudButton,
                            })}
                            key={item.id}
                            onClick={
                                disabled
                                    ? undefined
                                    : item.id === 'download' && IS_PUBLIC_FOLDER && showDownloadLimitsPromo
                                    ? onCustomClick
                                    : item.onClick
                            }
                            data-qa-action={item.id}
                        >
                            {item.component}
                            {item.icon && <div className={styles.icon}>{item.icon}</div>}
                            {item.text}
                        </div>
                    ))}
                </div>
                {showTip && (
                    <FeatureSelector
                        selector={() => abRequiredSignUpWhenDownloadingTouchSelector(false)}
                        control={<></>}
                        variant1={requiredSingUpTooltip}
                        variant2={requiredSingUpTooltip}
                    />
                )}
                {showDownloadPromo && (
                    <DownloadLimitsPromo
                        item={currentItem}
                        targetRef={downloadBtnRef?.current}
                        onClose={handleClickClose}
                        owner={isPublicOwner}
                    />
                )}
            </div>
        );
    }
);

ActionsCarousel.displayName = 'ActionsCarousel';
